import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { api, type Me } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

type Status = 'todo' | 'in-progress' | 'done' | 'archive';
type Priority = 'low' | 'normal' | 'high' | 'urgent';
interface TaskProject { id: number; parentId: number | null; name: string; description: string | null; team: number[]; pinnedAt: string | null; taskCount: number; unread: number }
interface Task { id: number; projectId: number; title: string; description: string | null; status: Status; priority: Priority; assignedTo: number | null; dueDate: string | null; calendarSync: string; createdAt: string }
interface Comment { id: number; userName: string; kind: 'comment' | 'log'; message: string; createdAt: string }
interface Activity { id: number; userName: string; action: string; details: string; createdAt: string }
interface TeamUser { id: number; name: string; email: string }

const SECTIONS: [Status, string, string][] = [['todo', 'Daryti', '📌'], ['in-progress', 'Vykdoma', '🚧'], ['done', 'Atlikta', '✅'], ['archive', 'Archyvas', '📦']];
const PRIORITIES: [Priority, string][] = [['low', 'Žemas'], ['normal', 'Įprastas'], ['high', 'Aukštas'], ['urgent', 'Skubus']];
const PALETTE = ['#3498db', '#e67e22', '#27ae60', '#9b59b6', '#e74c3c', '#16a085'];
const today = () => new Date().toLocaleDateString('sv-SE');
const initials = (name: string) => name.split(/\s+/).map((w) => w[0] ?? '').join('').slice(0, 2).toUpperCase();
const CALENDAR_WARNING = 'Užduotis išsaugota, bet Google kalendoriaus atnaujinti nepavyko. Norėdami bandyti dar kartą, redaguokite ir išsaugokite užduotį.';

/**
 * Užduotys as in the PHP panel: a full-height board with the project tree on the left, the project's
 * tasks in four folding sections (status changed with the small boxes on each row) and the latest
 * activity folded on the right. A task opens in a window to read, comment or edit.
 */
export function TasksPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<number | null>(null);
  const [projectDialog, setProjectDialog] = useState<TaskProject | 'new' | null>(null);
  const [openTask, setOpenTask] = useState<Task | 'new' | null>(null);
  const [folded, setFolded] = useState<Record<Status, boolean>>({ todo: false, 'in-progress': false, done: false, archive: true });
  const [warning, setWarning] = useState<string | null>(null);
  const projects = useQuery({ queryKey: ['task-projects'], queryFn: () => api.get<{ projects: TaskProject[] }>('api/v1/task-projects').then((r) => r.projects) });
  const team = useQuery({ queryKey: ['team'], queryFn: () => api.get<{ users: TeamUser[] }>('api/v1/team').then((r) => r.users) });
  const list = projects.data ?? [];
  // Pinned projects first, then by name; the first one opens by itself.
  const ordered = [...list].sort((a, b) => Number(Boolean(b.pinnedAt)) - Number(Boolean(a.pinnedAt)) || a.name.localeCompare(b.name));
  const projectId = chosen !== null && list.some((p) => p.id === chosen) ? chosen : ordered.find((p) => !p.parentId)?.id ?? ordered[0]?.id ?? null;
  const current = list.find((p) => p.id === projectId) ?? null;
  const tasks = useQuery({ queryKey: ['tasks', projectId], enabled: projectId !== null, queryFn: () => api.get<{ tasks: Task[] }>(`api/v1/tasks?projectId=${projectId}`).then((r) => r.tasks) });
  const refresh = async () => { await Promise.all(['tasks', 'task-projects', 'task-activities'].map((k) => queryClient.invalidateQueries({ queryKey: [k] }))); };
  useEffect(() => {
    if (projectId) void api.post(`api/v1/task-projects/${projectId}/viewed`).then(() => queryClient.invalidateQueries({ queryKey: ['task-projects'] }));
  }, [projectId, queryClient]);

  const move = useMutation({
    mutationFn: ({ id, status }: { id: number; status: Status }) => api.post<{ calendarWarning?: boolean }>(`api/v1/tasks/${id}/status`, { status }),
    onSuccess: async (r) => { setWarning(r.calendarWarning ? t(CALENDAR_WARNING) : null); await refresh(); },
  });
  const pin = useMutation({ mutationFn: (id: number) => api.post(`api/v1/task-projects/${id}/pin`), onSuccess: refresh });
  const users = team.data ?? [];
  const members = current ? users.filter((u) => current.team.includes(u.id)) : [];

  // The project tree: each project followed by its sub-projects, indented.
  const seen = new Set<number>();
  const tree = (nodes: TaskProject[], depth: number): ReactNode[] => nodes.filter((p) => !seen.has(p.id)).flatMap((p) => {
    seen.add(p.id);
    return [
      <div key={p.id} className={`tasks-nav-item${p.id === projectId ? ' active' : ''}${p.unread ? ' has-notification' : ''}${depth ? ' is-sub' : ''}`} style={{ paddingLeft: 12 + depth * 20 }}>
        <button type="button" className="tasks-nav-open" onClick={() => setChosen(p.id)}><span className="tasks-nav-dot" style={{ color: PALETTE[p.id % PALETTE.length] }}>●</span><span>{p.name}</span></button>
        <button type="button" className={`tasks-pin${p.pinnedAt ? ' pinned' : ''}`} title={p.pinnedAt ? t('Atsegti') : t('Prisegti')} aria-label={p.pinnedAt ? t('Atsegti') : t('Prisegti')} onClick={() => pin.mutate(p.id)}>{p.pinnedAt ? '📌' : '📍'}</button>
      </div>,
      ...tree(ordered.filter((c) => c.parentId === p.id), depth + 1),
    ];
  });
  const roots = ordered.filter((p) => !p.parentId || !list.some((x) => x.id === p.parentId));

  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Darbas')} title={t('Užduotys')} text={t('Projektai, užduotys ir veiklos istorija.')} />}>
      <section className="tasks-frame-panel tasks-app">
        {warning || move.isError ? <div className="tasks-alert"><Alert kind="error">{warning ?? errorText(t, move.error)}</Alert></div> : null}
        <div className="tasks-wrapper">
          <aside className="tasks-side">
            <div className="tasks-side-head">{t('Projektai')}</div>
            <nav className="tasks-nav">
              <div className="tasks-nav-section">{t('Mano projektai')}<button type="button" className="tasks-add" aria-label={t('Naujas projektas')} title={t('Naujas projektas')} onClick={() => setProjectDialog('new')}>+</button></div>
              {tree(roots, 0)}
              {!list.length && projects.isSuccess ? <p className="tasks-muted">{t('Sukurkite pirmąjį projektą, tada pridėkite užduočių.')}</p> : null}
              {users.length ? <div className="tasks-nav-section">{t('Komanda')}</div> : null}
              {users.map((u) => <div key={u.id} className="tasks-nav-item tasks-team"><span>👤</span><span>{u.name}</span></div>)}
            </nav>
          </aside>
          <main className="tasks-stage">
            <div className="tasks-stage-head">
              <h1>{current?.name ?? t('Užduotys')}</h1>
              <div className="tasks-stage-actions">
                <div className="tasks-avatars">{members.map((u) => <span key={u.id} title={u.name}>{initials(u.name)}</span>)}</div>
                {current ? <button type="button" className="tasks-btn tasks-btn--ghost" onClick={() => setProjectDialog(current)}>⚙️ {t('Nustatymai')}</button> : null}
                {current ? <button type="button" className="tasks-btn" onClick={() => setOpenTask('new')}>+ {t('Nauja užduotis')}</button> : null}
              </div>
            </div>
            {current?.description ? <p className="tasks-description">{current.description}</p> : null}
            <div className="tasks-list">
              {SECTIONS.map(([status, label, icon]) => {
                const rows = (tasks.data ?? []).filter((x) => x.status === status);
                return (
                  <div key={status} className="tasks-section">
                    <button type="button" className={`tasks-section-head is-${status}`} aria-expanded={!folded[status]} onClick={() => setFolded({ ...folded, [status]: !folded[status] })}>
                      <span>{icon} {t(label)} ({rows.length})</span><span>{folded[status] ? '+' : '−'}</span>
                    </button>
                    {folded[status] ? null : rows.map((task) => (
                      <div key={task.id} className={`tasks-row${task.status === 'done' || task.status === 'archive' ? ' is-done' : ''}`} role="button" tabIndex={0}
                        onClick={() => setOpenTask(task)} onKeyDown={(e) => { if (e.key === 'Enter') setOpenTask(task); }}>
                        <StatusSwitcher status={task.status} onChange={(s) => move.mutate({ id: task.id, status: s })} />
                        <div className="tasks-row-info">
                          <span className="tasks-id">#{task.id}</span>
                          <span className="tasks-title">{task.title}</span>
                          {task.calendarSync === 'pending' ? <span className="tasks-flag">⚠ {t('Kalendorius')}</span> : null}
                        </div>
                        {task.dueDate ? <small className={task.dueDate < today() && task.status !== 'done' && task.status !== 'archive' ? 'tasks-due is-late' : 'tasks-due'}>{task.dueDate}</small> : null}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </main>
          <ActivityRail />
        </div>
      </section>
      {projectDialog ? <ProjectDialog project={projectDialog === 'new' ? null : projectDialog} projects={list} users={users}
        onClose={async (removed) => { setProjectDialog(null); if (removed) setChosen(null); await refresh(); }} /> : null}
      {openTask && current ? <TaskDialog task={openTask === 'new' ? null : openTask} projects={list} users={users} defaultProject={current.id}
        onClose={async (calendarWarning) => { setOpenTask(null); setWarning(calendarWarning ? t(CALENDAR_WARNING) : null); await refresh(); }} /> : null}
    </PanelShell>
  );
}

/** The row's small coloured boxes: to do, in progress, done (and archive once done). */
function StatusSwitcher({ status, onChange }: { status: Status; onChange: (status: Status) => void }) {
  const { t } = useI18n();
  const boxes: Status[] = status === 'done' || status === 'archive' ? ['todo', 'in-progress', 'done', 'archive'] : ['todo', 'in-progress', 'done'];
  return (
    <div className="tasks-switcher" onClick={(e) => e.stopPropagation()}>
      {boxes.map((s) => (
        <button key={s} type="button" className={`tasks-box s-${s}${status === s ? ' active' : ''}`} title={t(SECTIONS.find(([k]) => k === s)![1])} aria-label={t(SECTIONS.find(([k]) => k === s)![1])} aria-pressed={status === s}
          onClick={() => { if (status !== s) onChange(s); }}>✓</button>
      ))}
    </div>
  );
}

function ActivityRail() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const activity = useQuery({ queryKey: ['task-activities'], enabled: open, queryFn: () => api.get<{ activities: Activity[] }>('api/v1/task-activities').then((r) => r.activities) });
  return (
    <details className="tasks-log" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary><span>{t('Naujausia veikla')}</span></summary>
      <div className="tasks-log-list">
        {(activity.data ?? []).slice(0, 20).map((a) => (
          <div key={a.id} className="tasks-log-item">
            <small>{new Date(a.createdAt).toLocaleString(undefined, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} • {a.userName}</small>
            <div>{t(a.action)}</div>
            {a.details ? <div className="tasks-log-details">{a.details.length > 53 ? `${a.details.slice(0, 53)}…` : a.details}</div> : null}
          </div>
        ))}
      </div>
    </details>
  );
}

function Modal({ className, onClose, children }: { className?: string; onClose: () => void; children: ReactNode }) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => { dialog.current?.showModal(); }, []);
  return <dialog ref={dialog} className={`confirm-dialog tasks-modal ${className ?? ''}`} onClose={onClose}><div className="dialog-card">{children}</div></dialog>;
}

function ProjectDialog({ project, projects, users, onClose }: { project: TaskProject | null; projects: TaskProject[]; users: TeamUser[]; onClose: (removed?: boolean) => void }) {
  const { t } = useI18n();
  const save = useMutation({ mutationFn: (body: unknown) => (project ? api.put(`api/v1/task-projects/${project.id}`, body) : api.post('api/v1/task-projects', body)), onSuccess: () => onClose() });
  const remove = useMutation({ mutationFn: () => api.delete(`api/v1/task-projects/${project!.id}`), onSuccess: () => onClose(true) });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const d = new FormData(event.currentTarget);
    save.mutate({ name: d.get('name'), description: d.get('description'), parentId: d.get('parentId') ? Number(d.get('parentId')) : null, team: d.getAll('team').map(Number) });
  };
  return (
    <Modal onClose={() => onClose()}>
      <form onSubmit={submit}>
        <p className="eyebrow">{project ? t('Projekto nustatymai') : t('Naujas projektas')}</p>
        {save.isError || remove.isError ? <Alert kind="error">{errorText(t, save.error ?? remove.error)}</Alert> : null}
        <label>{t('Pavadinimas')}<input name="name" defaultValue={project?.name ?? ''} required autoFocus /></label>
        <label>{t('Aprašymas')}<textarea name="description" rows={2} defaultValue={project?.description ?? ''} /></label>
        <label>{t('Pagrindinis projektas')}<select name="parentId" defaultValue={project?.parentId ?? ''}><option value="">—</option>{projects.filter((p) => p.id !== project?.id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
        {users.length > 1 ? <fieldset className="plain-fieldset"><p className="form-section">{t('Komanda')}</p>{users.map((u) => (
          <label key={u.id} className="remember-device"><input type="checkbox" name="team" value={u.id} defaultChecked={project?.team.includes(u.id)} /><span>{u.name}</span></label>
        ))}</fieldset> : null}
        <div className="dialog-actions">
          {project ? <button type="button" className="button button--danger" onClick={() => { if (confirm(t('Pašalinti projektą „{name}“ ir visas jo užduotis?', { name: project.name }))) remove.mutate(); }}>{t('Pašalinti')}</button> : null}
          <button type="button" className="button button--ghost" onClick={() => onClose()}>{t('Atšaukti')}</button>
          <button className="button button--primary" disabled={save.isPending}>{t('Išsaugoti')}</button>
        </div>
      </form>
    </Modal>
  );
}

function TaskDialog({ task, projects, users, defaultProject, onClose }: { task: Task | null; projects: TaskProject[]; users: TeamUser[]; defaultProject: number; onClose: (calendarWarning?: boolean) => void }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(!task);
  const [more, setMore] = useState(false);
  const [tab, setTab] = useState<'comment' | 'log'>('comment');
  const [status, setStatus] = useState<Status | undefined>(task?.status);
  const detail = useQuery({ queryKey: ['task', task?.id], enabled: Boolean(task), queryFn: () => api.get<{ comments: Comment[] }>(`api/v1/tasks/${task!.id}`).then((r) => r.comments) });
  const save = useMutation({
    mutationFn: (body: unknown) => (task ? api.put<{ calendarWarning?: boolean }>(`api/v1/tasks/${task.id}`, body) : api.post<{ calendarWarning?: boolean }>('api/v1/tasks', body)),
    onSuccess: (r) => onClose(r.calendarWarning),
  });
  const move = useMutation({
    mutationFn: (s: Status) => api.post(`api/v1/tasks/${task!.id}/status`, { status: s }),
    onSuccess: async (_r, s) => { setStatus(s); await Promise.all([detail.refetch(), queryClient.invalidateQueries({ queryKey: ['tasks'] })]); },
  });
  const remove = useMutation({ mutationFn: () => api.delete(`api/v1/tasks/${task!.id}`), onSuccess: () => onClose() });
  const comment = useMutation({ mutationFn: (message: string) => api.post(`api/v1/tasks/${task!.id}/comments`, { message }), onSuccess: () => detail.refetch() });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const d = new FormData(event.currentTarget);
    save.mutate({ projectId: Number(d.get('projectId') ?? defaultProject), title: d.get('title'), description: d.get('description') ?? task?.description ?? '', dueDate: d.get('dueDate') || null,
      priority: d.get('priority') ?? 'normal', assignedTo: d.get('assignedTo') ? Number(d.get('assignedTo')) : null });
  };
  const priority = task ? PRIORITIES.find(([k]) => k === task.priority)?.[1] ?? '' : '';
  const project = task ? projects.find((p) => p.id === task.projectId)?.name : '';
  const error = save.error ?? remove.error ?? move.error ?? comment.error;

  if (task && !editing) {
    const comments = (detail.data ?? []).filter((c) => (tab === 'comment' ? c.kind === 'comment' : c.kind === 'log'));
    return (
      <Modal className="tasks-modal--view" onClose={() => onClose()}>
        {error ? <Alert kind="error">{errorText(t, error)}</Alert> : null}
        <div className="tasks-view-head"><h2>{task.title}</h2><button type="button" className="delete-x" aria-label={t('Uždaryti')} onClick={() => onClose()}>×</button></div>
        <div className="tasks-view-meta">
          <StatusSwitcher status={status ?? task.status} onChange={(s) => move.mutate(s)} />
          {task.dueDate ? <span>📅 {task.dueDate}</span> : null}
          {project ? <span>📂 {project}</span> : null}
          <span className={`tasks-pill is-${task.priority}`}>🚨 {t(priority)}</span>
          {task.assignedTo ? <span>👤 {users.find((u) => u.id === task.assignedTo)?.name}</span> : null}
        </div>
        <div className="tasks-view-description">{task.description || <span className="tasks-muted">{t('Aprašymo nėra.')}</span>}</div>
        <div className="tasks-tabs">
          <button type="button" className={tab === 'comment' ? 'active' : ''} onClick={() => setTab('comment')}>{t('Komentarai')}</button>
          <button type="button" className={tab === 'log' ? 'active' : ''} onClick={() => setTab('log')}>{t('Veiklos žurnalas')}</button>
        </div>
        <div className="tasks-comments">
          {comments.map((c) => <p key={c.id}><b>{c.userName}</b> <small>{new Date(c.createdAt).toLocaleString()}</small><br />{c.message}</p>)}
          {!comments.length ? <p className="tasks-muted">{tab === 'comment' ? t('Komentarų dar nėra.') : t('Įrašų dar nėra.')}</p> : null}
        </div>
        {tab === 'comment' ? (
          <form onSubmit={(e) => { e.preventDefault(); const input = e.currentTarget.elements.namedItem('message') as HTMLInputElement; if (input.value.trim()) { comment.mutate(input.value); input.value = ''; } }}>
            <input name="message" className="tasks-comment-input" placeholder={t('Rašyti komentarą… (Enter — siųsti)')} />
          </form>
        ) : null}
        <div className="dialog-actions"><button type="button" className="button button--ghost" onClick={() => setEditing(true)}>✎ {t('Redaguoti užduotį')}</button></div>
      </Modal>
    );
  }

  return (
    <Modal onClose={() => onClose()}>
      <form onSubmit={submit}>
        <p className="eyebrow">{task ? t('Redaguoti užduotį') : t('Nauja užduotis')}</p>
        {error ? <Alert kind="error">{errorText(t, error)}</Alert> : null}
        <label>{t('Pavadinimas')}<input name="title" defaultValue={task?.title ?? ''} required autoFocus /></label>
        {task || more ? <>
          <div className="form-grid">
            <label>{t('Projektas')}<select name="projectId" defaultValue={task?.projectId ?? defaultProject}>{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
            <label>{t('Terminas')}<input type="date" name="dueDate" defaultValue={task?.dueDate ?? ''} /></label>
            <label>{t('Prioritetas')}<select name="priority" defaultValue={task?.priority ?? 'normal'}>{PRIORITIES.map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}</select></label>
            <label>{t('Atsakingas')}<select name="assignedTo" defaultValue={task?.assignedTo ?? ''}><option value="">—</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
          </div>
          <label>{t('Aprašymas')}<textarea name="description" rows={4} defaultValue={task?.description ?? ''} /></label>
        </> : <button type="button" className="text-link tasks-more" onClick={() => setMore(true)}>+ {t('Daugiau informacijos')}</button>}
        <div className="dialog-actions">
          {task ? <button type="button" className="button button--danger" onClick={() => { if (confirm(t('Pašalinti užduotį?'))) remove.mutate(); }}>{t('Pašalinti')}</button> : null}
          <button type="button" className="button button--ghost" onClick={() => (task ? setEditing(false) : onClose())}>{t('Atšaukti')}</button>
          <button type="submit" className="button button--primary" disabled={save.isPending}>{t('Išsaugoti')}</button>
        </div>
      </form>
    </Modal>
  );
}
